import React from "react";
import { ColoredValue } from "@src/components/ColoredValue";
import { CSpinnerText } from "@tradingdroid/frontends-components";
import type * as T from "@src/types";
import { CANCELABLE_ORDER_STATUSES, TIME_IN_FORCE_IOC } from "@src/utils/constants";
import {
  calculateUnrealizedPnl,
  calculateUnrealizedPnlPercent,
  getLatestPriceFromCandles,
} from "@src/utils/executionUtils";

import { CancelLimitOrderButton } from "./CancelLimitOrderButton";
import { ClosePositionButton } from "./ClosePositionButton";
import { useRunResultContext } from "./RunResultContext";

interface Props {
  position: T.AppPosition;
  isLive: boolean;
  executionId?: number;
}

// The cancel-limit-order command settles an entry and refuses any other order. An order with no role
// counts as an entry when it is on the entry side (buy for a long, sell for a short), the same rule
// the worker applies before it cancels.
const isEntryOrder = (
  order: T.TDT.ApiOrder,
  directionName: T.AppPosition["direction_name"],
): boolean => {
  if (order.role_name != null) {
    return order.role_name === "entry";
  }
  return order.order_side_name === (directionName === "long" ? "buy" : "sell");
};

/**
 * Displays the state of an open position (no close movement yet)
 * Renders both left (status/PnL) and right (action buttons) columns
 */
export const OpenPositionCell: React.FC<Props> = ({ position, isLive, executionId }) => {
  const { datafeedRef } = useRunResultContext();
  const [currentPrice, setCurrentPrice] = React.useState<number>(0);

  // Get latest price from datafeed (candles or API fallback state)
  // Polls until the chart provides a price - works with both cache and API fallback mode.
  // When cache is unavailable, the chart switches to API fallback ("Live chart mode")
  // and getBars sets datafeed.state.latestPrice from the fetched candles.
  React.useEffect(() => {
    const checkPrice = () => {
      // First try pre-loaded candles
      const candlesPrice = getLatestPriceFromCandles(datafeedRef.current?.props?.candles);
      if (candlesPrice > 0) {
        setCurrentPrice(candlesPrice);
        return true;
      }
      // Then try API fallback state (set by getBars when chart uses API mode)
      const fallbackPrice = datafeedRef.current?.state?.latestPrice;
      if (fallbackPrice && fallbackPrice > 0) {
        setCurrentPrice(fallbackPrice);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkPrice()) return;

    // Poll until the chart loads data (cache or API fallback mode)
    // Cap at 60s to avoid infinite polling on network failures
    let retries = 0;
    const MAX_RETRIES = 120; // 120 × 500ms = 60s
    const interval = setInterval(() => {
      if (checkPrice() || ++retries >= MAX_RETRIES) {
        clearInterval(interval);
      }
    }, 500);

    return () => clearInterval(interval);
  }, [datafeedRef, position.id]);

  // Check for a pending entry limit order in movements
  // Note: Some exchanges (e.g., Hyperliquid) send market orders as IOC (Immediate-or-Cancel) limit orders.
  // These fill immediately and should NOT be treated as "pending limit orders".
  // We detect this by checking timeInForce === "IOC" in exchange_raw_data.
  // This check applies to all exchanges since IOC semantics are standard (execute immediately or cancel).
  // If other exchanges behave differently, we can add exchange-specific logic later.
  const pendingEntryMovement = position.movements?.find((m) => {
    const order = m.order;
    if (!order || order.order_type_name !== "limit" || !order.order_status_name) {
      return false;
    }

    // IOC orders are market-like: they fill immediately or cancel, never truly "pending".
    // First observed with Hyperliquid, but applies generally to IOC order semantics.
    const timeInForce = order.exchange_raw_data?.timeInForce;
    if (timeInForce === TIME_IN_FORCE_IOC) {
      return false;
    }

    if (!isEntryOrder(order, position.direction_name)) {
      return false;
    }

    return (CANCELABLE_ORDER_STATUSES as readonly string[]).includes(
      order.order_status_name.toLowerCase(),
    );
  });
  const pendingEntryOrder = pendingEntryMovement ? pendingEntryMovement.order : null;

  // Calculate unrealized PnL if we have a current price
  const unrealizedPnl = currentPrice
    ? calculateUnrealizedPnl({
        entryPrice: position.entry_price,
        currentPrice,
        currentAmount: position.current_amount,
        directionName: position.direction_name,
        typeName: position.type_name,
        collateralIn: position.collateral_in ?? null,
      })
    : 0;
  const unrealizedPnlPercent = calculateUnrealizedPnlPercent(
    unrealizedPnl,
    position.collateral_in ?? null,
  );

  const renderLeftColumn = () => {
    if (pendingEntryOrder?.id) {
      return (
        <div className="OpenPositionCell flex-column">
          <div className="d-flex justify-content-between data-value-row mb-2">
            <strong className="text-warning">Pending limit order</strong>
          </div>
        </div>
      );
    }

    return (
      <div className="OpenPositionCell flex-column">
        <div className="d-flex justify-content-between data-value-row mb-2">
          <strong className="text-muted">Position still open</strong>
        </div>
        {currentPrice > 0 ? (
          <>
            <div className="d-flex justify-content-between data-value-row">
              <strong>Price:</strong>
              <span>
                ~<ColoredValue value={currentPrice} type="usdt" decimals={2} />
              </span>
            </div>
            <div className="d-flex justify-content-between data-value-row">
              <strong>Est. PnL:</strong>
              <ColoredValue value={unrealizedPnl} type="usdt" />
            </div>
            <div className="d-flex justify-content-between data-value-row">
              <strong>Est. PnL %:</strong>
              <ColoredValue value={unrealizedPnlPercent} type="percentage" />
            </div>
          </>
        ) : (
          isLive && (
            <div className="data-value-row">
              <CSpinnerText
                text="Loading price data for unrealized PnL..."
                textClassName="text-muted"
              />
            </div>
          )
        )}
      </div>
    );
  };

  const renderRightColumn = () => {
    if (!isLive || !executionId) return null;

    return (
      <div className="OpenPositionCell d-flex justify-content-end align-items-start w-100">
        {pendingEntryOrder?.id ? (
          <CancelLimitOrderButton
            executionId={executionId}
            positionId={position.id}
            orderId={pendingEntryOrder.id}
            symbol={position.symbol_name}
          />
        ) : (
          <ClosePositionButton
            executionId={executionId}
            positionId={position.id}
            positionSize={position.current_amount}
            symbol={position.symbol_name}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {renderLeftColumn()}
      {renderRightColumn()}
    </>
  );
};
